import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { AdminDashboardApi } from '../../core/api/dashboard/admin-dashboard.api';
import { DashboardStats } from '../../core/api/dashboard/admin-dashboard.models';
import { AdminAuthService } from '../../core/auth/admin-auth.service';

interface StatCard {
  title: string;
  value: number;
  icon: string;
  accentClass: string;
  description: string;
}

@Component({
  selector: 'app-dashboard',
  imports: [CommonModule, RouterLink, MatButtonModule, MatCardModule, MatIconModule],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent {
  private readonly dashboardApi = inject(AdminDashboardApi);
  private readonly authService = inject(AdminAuthService);

  readonly stats = signal<DashboardStats>({
    totalUsers: 0,
    totalLeads: 0,
    totalPublishedProperties: 0,
    totalCallbackRequests: 0,
  });
  readonly isLoading = signal(false);
  readonly errorMessage = signal('');
  readonly lastUpdated = signal<Date | null>(null);
  readonly currentUser = this.authService.getCurrentUser();

  readonly quickLinks = [
    {
      title: 'Review Properties',
      description: 'Manage published property inventory and listing quality.',
      icon: 'home_work',
      route: '/property/rent/manage',
      actionLabel: 'Open properties',
    },
    {
      title: 'Track Leads',
      description: 'Monitor owner and tenant lead flow from one place.',
      icon: 'groups',
      route: '/leads/tenants',
      actionLabel: 'View leads',
    },
    {
      title: 'Handle Callbacks',
      description: 'Follow up on owner and contact-us callback requests.',
      icon: 'support_agent',
      route: '/users/owner-call-requests',
      actionLabel: 'Open callbacks',
    },
  ];

  ngOnInit(): void {
    this.loadStats();
  }

  loadStats(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.dashboardApi
      .getStats()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.errorMessage.set(response.message || 'Unable to fetch dashboard stats.');
            return;
          }

          this.stats.set(response.data);
          this.lastUpdated.set(new Date());
        },
        error: (error) => {
          this.errorMessage.set(error?.error?.message || 'Something went wrong while loading dashboard stats.');
        },
      });
  }

  get statCards(): StatCard[] {
    const stats = this.stats();

    return [
      {
        title: 'Active Users',
        value: stats.totalUsers,
        icon: 'group',
        accentClass: 'users-card',
        description: 'Admin accounts are excluded from this number.',
      },
      {
        title: 'Leads Captured',
        value: stats.totalLeads,
        icon: 'analytics',
        accentClass: 'leads-card',
        description: 'Combined lead volume across the admin platform.',
      },
      {
        title: 'Published Properties',
        value: stats.totalPublishedProperties,
        icon: 'domain',
        accentClass: 'properties-card',
        description: 'Live property listings currently visible to users.',
      },
      {
        title: 'Callback Requests',
        value: stats.totalCallbackRequests,
        icon: 'headset_mic',
        accentClass: 'callbacks-card',
        description: 'Includes owner callback and contact-us callback requests.',
      },
    ];
  }

  formatTimestamp(value: Date | null): string {
    if (!value) {
      return 'Waiting for first sync';
    }

    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(value);
  }
}
